<template lang="pug">
.sticker-collection-detail-tabs(:class="`layout-${layout}`")
  .detail-tab-bar
    button(
      v-for="tab in tabOptions"
      :key="tab.id"
      type="button"
      :class="['detail-tab', { active: activeTab === tab.id }]"
      @click="setTab(tab.id)"
    ) {{ tab.label }}

  .detail-tab-body
    div(v-show="activeTab === 'stickers'" class="stickers-panel")
      p.empty-stickers(v-if="!sortedItems.length") No stickers in this collection yet.
      .stickers-grid-wrap(v-else)
        sticker-collection-grid-item(
          v-for="entry in sortedItems"
          :key="entry.id"
          :item="entry"
          :compact="layout === 'panel'"
          :large="layout === 'page'"
          @select="onSelectSticker"
        )

    div(v-show="activeTab === 'updates'" class="updates-panel")
      .creator-note(v-if="collection.creatorNote")
        p.note-label A note from {{ collection.ownerName || 'the creator' }}
        p.note-body "{{ collection.creatorNote }}"
      p.no-updates(v-else-if="!sortedUpdates.length") No updates yet.

      .updates-timeline(v-if="sortedUpdates.length")
        .update-row(v-for="(update, index) in sortedUpdates" :key="update.id")
          .update-rail
            .update-dot
            .update-line(v-if="index < sortedUpdates.length - 1")
          div
            p.update-date {{ formatDate(update.postedAt) }}
            p.update-body {{ update.body }}
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import StickerCollectionGridItem from '@/views/web-app/sticker/components/StickerCollectionGridItem.vue';
import type {
  StickerCollectionItem,
  StickerCollectionStickerRef,
  StickerCollectionView,
} from '@/data/stickerCollection.types';

type DetailTab = 'stickers' | 'updates';

export default defineComponent({
  name: 'StickerCollectionDetailTabs',
  components: { StickerCollectionGridItem },
  props: {
    collection: {
      type: Object as () => StickerCollectionView,
      required: true,
    },
    modelValue: {
      type: String as () => DetailTab,
      default: 'stickers',
    },
    layout: {
      type: String as () => 'panel' | 'page',
      default: 'page',
    },
  },
  emits: ['update:modelValue', 'select-sticker'],
  setup(props, { emit }) {
    const activeTab = ref<DetailTab>(props.modelValue);

    watch(
      () => props.modelValue,
      (value) => {
        activeTab.value = value;
      },
    );

    const tabOptions = [
      { id: 'stickers' as const, label: 'Stickers' },
      { id: 'updates' as const, label: 'Updates' },
    ];

    const sortedItems = computed((): StickerCollectionItem[] => {
      const items = [...(props.collection.items || [])];
      items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      return items.map((item) => ({
        ...item,
        sortOrder: item.sortOrder ?? 0,
      }));
    });

    const sortedUpdates = computed(() => {
      const updates = [...(props.collection.updates || [])];
      updates.sort(
        (a, b) => dayjs(b.postedAt).valueOf() - dayjs(a.postedAt).valueOf(),
      );
      return updates;
    });

    function setTab(tab: DetailTab) {
      activeTab.value = tab;
      emit('update:modelValue', tab);
    }

    function formatDate(value: string) {
      return dayjs(value).format('MMM D');
    }

    function onSelectSticker(sticker: StickerCollectionStickerRef) {
      emit('select-sticker', sticker);
    }

    return {
      activeTab,
      tabOptions,
      sortedItems,
      sortedUpdates,
      setTab,
      formatDate,
      onSelectSticker,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/web-tabs';

.detail-tab-bar {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
}

.detail-tab {
  @include web-tab;

  flex: 1;
}

.layout-page {
  .detail-tab-bar {
    gap: 0.25rem;
    margin-top: 1.25rem;
    padding: 0.25rem;
    border: none;
    border-radius: 999px;
    background: #f3f4f6;
  }

  .detail-tab {
    border-radius: 999px;
    padding: 0.5rem;
    transition: background 0.15s ease, color 0.15s ease;

    &.active {
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      &::after {
        display: none;
      }
    }
  }

  .stickers-grid-wrap {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(6.5rem, 1fr));
    gap: 1.25rem 1rem;
  }
}

.layout-panel {
  .detail-tab {
    flex: 0 0 auto;
    margin-right: 1.25rem;
    padding: 0 0 0.5rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;

    &.active::after {
      left: 0;
      right: 0;
      background: #0d9488;
    }
  }

  .stickers-grid-wrap {
    gap: 0.5rem;
  }
}

.detail-tab-body {
  padding: 1rem 0;
}

.layout-panel .detail-tab-body {
  padding: 1rem;
}

.empty-stickers {
  margin: 0;
  font-size: 0.8125rem;
  color: #9ca3af;
  text-align: center;
  padding: 1.5rem 0;
}

.stickers-grid-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.updates-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.creator-note {
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 0.75rem;
  padding: 0.75rem;
}

.layout-page .creator-note {
  background: #fff;
}

.note-label {
  margin: 0 0 0.375rem;
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #0d9488;
}

.note-body {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.625;
  color: #4b5563;
  font-style: italic;
}

.no-updates {
  margin: 0;
  font-size: 0.75rem;
  color: #9ca3af;
}

.updates-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.update-row {
  display: flex;
  gap: 0.75rem;
}

.update-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.update-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #0d9488;
  margin-top: 0.25rem;
}

.update-line {
  width: 1px;
  flex: 1;
  background: #e5e7eb;
}

.update-date {
  margin: 0 0 0.125rem;
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ca3af;
}

.update-body {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.625;
  color: #4b5563;
}
</style>
